// ============================================================
// FindingsReport.jsx
// Step 5: Full findings and action item tracker
// ============================================================

import React, { useState } from "react";
import { calcCategoryStats, scoreToClass, scoreToRating } from "../utils/calculations";
import { exportToCSV, exportActionItemsCSV, exportToJSON } from "../utils/exportUtils";

const STATUS_COLORS = {
  Compliant: "badge-compliant",
  "Partially Compliant": "badge-partial",
  "Non-Compliant": "badge-noncompliant",
  "Not Applicable": "badge-na",
};
const PRIORITY_COLORS = {
  Immediate: "badge-immediate",
  High: "badge-high",
  Medium: "badge-medium",
  Low: "badge-low",
};
const COMPLETION_OPTIONS = ["", "Open", "In Progress", "Closed"];
const PRIORITY_OPTIONS = ["", "Immediate", "High", "Medium", "Low"];

export default function FindingsReport({ auditInfo, categories, responses, onChange, onBack, onSave, onNew }) {
  const [filterStatus, setFilterStatus] = useState("Action Items Only");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterProgram, setFilterProgram] = useState("");
  const [expandedCats, setExpandedCats] = useState(new Set(categories.map((c) => c.id)));

  const toggleCat = (id) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedCats(new Set(categories.map((c) => c.id)));
  const collapseAll = () => setExpandedCats(new Set());

  // Filter criteria based on selected filters
  const filterCriteria = (criteria, catId) => {
    return criteria.filter((crit) => {
      const r = responses[crit.id] || {};

      if (filterStatus === "Action Items Only") {
        if (
          r.status !== "Non-Compliant" &&
          r.status !== "Partially Compliant" &&
          !r.actionItem?.trim()
        ) return false;
      } else if (filterStatus && filterStatus !== "All Items") {
        if (r.status !== filterStatus) return false;
      }

      if (filterPriority && r.priority !== filterPriority) return false;
      if (filterProgram && catId !== filterProgram) return false;

      return true;
    });
  };

  // Count action items
  const totalActionItems = categories.reduce((sum, cat) => {
    return (
      sum +
      cat.criteria.filter((crit) => {
        const r = responses[crit.id] || {};
        return (
          r.status === "Non-Compliant" ||
          r.status === "Partially Compliant" ||
          r.actionItem?.trim()
        );
      }).length
    );
  }, 0);

  return (
    <div className="page-container print-full">
      {/* Print report header */}
      <div className="print-report-header">
        <div className="print-logo">OSHA GENERAL INDUSTRY COMPLIANCE AUDIT</div>
        <div className="print-company">{auditInfo.companyName || "—"}</div>
        <div className="print-meta">
          {auditInfo.facilityName && <>{auditInfo.facilityName} &nbsp;·&nbsp;</>}
          {auditInfo.facilityAddress && <>{auditInfo.facilityAddress} &nbsp;·&nbsp;</>}
          Audit Date: {auditInfo.auditDate} &nbsp;·&nbsp; Auditor: {auditInfo.auditorName || "—"}
          {auditInfo.auditType && <> &nbsp;·&nbsp; Type: {auditInfo.auditType}</>}
        </div>
        {auditInfo.scope && <div className="print-scope">Scope: {auditInfo.scope}</div>}
      </div>

      <div className="page-header no-print">
        <h1 className="page-title">Findings & Action Item Report</h1>
        <p className="page-desc">
          {totalActionItems} action item{totalActionItems !== 1 ? "s" : ""} identified across{" "}
          {categories.length} program{categories.length !== 1 ? "s" : ""}.
          Tracking fields (Responsible Person, Due Date, Completion) are for client use.
        </p>
      </div>

      {/* Export + Print toolbar */}
      <div className="toolbar no-print">
        <button className="btn btn-outline btn-sm" onClick={() => exportToCSV(auditInfo, categories, responses)}>
          ↓ Full Audit CSV
        </button>
        <button className="btn btn-outline btn-sm" onClick={() => exportActionItemsCSV(auditInfo, categories, responses)}>
          ↓ Action Items CSV
        </button>
        <button className="btn btn-outline btn-sm" onClick={() => exportToJSON(auditInfo, categories, responses)}>
          ↓ JSON Export
        </button>
        <button className="btn btn-outline btn-sm" onClick={() => window.print()}>
          🖨 Print / PDF Report
        </button>
      </div>

      {/* Filters */}
      <div className="card no-print">
        <div className="filters-row">
          <div className="field-group">
            <label className="field-label">Filter by Status</label>
            <select className="field-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="All Items">All Items</option>
              <option value="Action Items Only">Action Items Only</option>
              <option value="Non-Compliant">Non-Compliant</option>
              <option value="Partially Compliant">Partially Compliant</option>
              <option value="Compliant">Compliant</option>
              <option value="Not Applicable">Not Applicable</option>
            </select>
          </div>
          <div className="field-group">
            <label className="field-label">Filter by Priority</label>
            <select className="field-select" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
              <option value="">All Priorities</option>
              {PRIORITY_OPTIONS.filter(Boolean).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="field-group">
            <label className="field-label">Filter by Program</label>
            <select className="field-select" value={filterProgram} onChange={(e) => setFilterProgram(e.target.value)}>
              <option value="">All Programs</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="field-group btn-align">
            <button className="btn-text" onClick={expandAll}>Expand All</button>
            <span className="divider">|</span>
            <button className="btn-text" onClick={collapseAll}>Collapse All</button>
          </div>
        </div>
      </div>

      {/* Category sections */}
      {categories.map((cat) => {
        const visibleCriteria = filterCriteria(cat.criteria, cat.id);
        if (visibleCriteria.length === 0) return null;

        const stats = calcCategoryStats(cat, responses);
        const isExpanded = expandedCats.has(cat.id);

        return (
          <div key={cat.id} className="findings-section">
            {/* Category header */}
            <div
              className="findings-cat-header"
              onClick={() => toggleCat(cat.id)}
            >
              <div className="findings-cat-left">
                <span className="findings-toggle">{isExpanded ? "▼" : "▶"}</span>
                <div>
                  <div className="findings-cat-name">{cat.name}</div>
                  <div className="findings-cat-citation">{cat.citation}</div>
                </div>
              </div>
              <div className="findings-cat-right">
                {stats.score !== null && (
                  <span className={`score-pill ${scoreToClass(stats.score)}`}>
                    {stats.score}% — {scoreToRating(stats.score)}
                  </span>
                )}
                <span className="findings-item-count">
                  {visibleCriteria.length} item{visibleCriteria.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>

            {isExpanded && (
              <div className="findings-items">
                {visibleCriteria.map((crit) => {
                  const r = responses[crit.id] || {};
                  return (
                    <div key={crit.id} className={`finding-item ${r.status === "Non-Compliant" ? "finding-noncompliant" : r.status === "Partially Compliant" ? "finding-partial" : ""}`}>
                      {/* Finding header row */}
                      <div className="finding-header-row">
                        <div className="finding-header-left">
                          <span className="finding-citation">{crit.citation}</span>
                          <span className="finding-req">{crit.requirement}</span>
                        </div>
                        <div className="finding-badges">
                          {r.status && (
                            <span className={`badge ${STATUS_COLORS[r.status] || ""}`}>
                              {r.status}
                            </span>
                          )}
                          {r.priority && (
                            <span className={`badge ${PRIORITY_COLORS[r.priority] || ""}`}>
                              {r.priority}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Notes */}
                      {r.notes && (
                        <div className="finding-block">
                          <div className="finding-block-label">Auditor Notes</div>
                          <div className="finding-block-text">{r.notes}</div>
                        </div>
                      )}

                      {/* Action item */}
                      {r.actionItem && (
                        <div className="finding-block finding-action">
                          <div className="finding-block-label">Action Item</div>
                          <div className="finding-block-text">{r.actionItem}</div>
                        </div>
                      )}

                      {/* Tracking row — editable inline */}
                      <div className="finding-tracking no-print">
                        <div className="tracking-label">Client Tracking</div>
                        <div className="finding-tracking-fields">
                          <div className="field-group-sm">
                            <label className="field-label-sm">Responsible Person</label>
                            <input
                              className="field-input-sm"
                              placeholder="Name / Title"
                              value={r.responsiblePerson || ""}
                              onChange={(e) => onChange(crit.id, "responsiblePerson", e.target.value)}
                            />
                          </div>
                          <div className="field-group-sm">
                            <label className="field-label-sm">Due Date</label>
                            <input
                              className="field-input-sm"
                              type="date"
                              value={r.dueDate || ""}
                              onChange={(e) => onChange(crit.id, "dueDate", e.target.value)}
                            />
                          </div>
                          <div className="field-group-sm">
                            <label className="field-label-sm">Completion Status</label>
                            <select
                              className="field-input-sm"
                              value={r.completionStatus || ""}
                              onChange={(e) => onChange(crit.id, "completionStatus", e.target.value)}
                            >
                              {COMPLETION_OPTIONS.map((o) => (
                                <option key={o} value={o}>{o || "— Not Set —"}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Print-only tracking row */}
                      <div className="finding-tracking-print print-only">
                        <span><strong>Responsible:</strong> {r.responsiblePerson || "_____________________"}</span>
                        <span><strong>Due:</strong> {r.dueDate || "__________"}</span>
                        <span><strong>Status:</strong> {r.completionStatus || "____________"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Print signature block */}
      <div className="print-signature print-only">
        <div className="sig-block">
          <div className="sig-line" />
          <div className="sig-label">Auditor Signature / Date</div>
        </div>
        <div className="sig-block">
          <div className="sig-line" />
          <div className="sig-label">Client Representative Signature / Date</div>
        </div>
      </div>

      <div className="btn-row no-print">
        <button className="btn btn-secondary" onClick={onBack}>← Back to Dashboard</button>
        <div style={{ display: "flex", gap: "10px" }}>
          <button className="btn btn-outline" onClick={onSave}>
            💾 Save Audit
          </button>
          <button className="btn btn-primary" onClick={onNew}>
            ＋ New Audit / Manage Audits
          </button>
        </div>
      </div>
    </div>
  );
}
